import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/logo_new.png";
import profile from "../../assets/profile_new.png";
import offer1 from "../../assets/offer1.png";
import offer2 from "../../assets/offer2.png";
import offer3 from "../../assets/offer3.png";
import bundle from "../../assets/bundle.png";
import { motion } from "framer-motion";
import axios from "axios";
import {
  AppchargeCheckout,
  getPricePoints,
} from "appcharge-checkout-reactjs-sdk";
import {
  Box,
  Button,
  Drawer,
  Input,
  Stack,
  Switch,
  Typography,
  FormControlLabel,
} from "@mui/material";
import JSONInput from "react-json-editor-ajrm";
import { getInfraDev, getRuntimeEnv } from "../../runtimeConfig";
import Subscriptions from "../Subscriptions/Subscriptions";

const CHECKOUT_V3_URL_MAP = {
  dev: "https://pay-dev.appcharge.com",
  staging: "https://pay-staging.appcharge.com",
  sandbox: "https://pay-sandbox.appcharge.com",
  production: "https://pay.appcharge.com",
};

const env = getRuntimeEnv("REACT_APP_ENV", "dev");
const publisherToken =
  getRuntimeEnv("REACT_APP_PUBLISHER_TOKEN") ||
  "5fe9908d778bee1cc6aa3f22b05442ab367600fb118ea8d8509dd4365347f2d0";
const checkoutToken =
  getRuntimeEnv("REACT_APP_CHECKOUT_TOKEN") ||
  "85c6be660eaf1871ceeadebb6d2219cc52d395b8c37f72fe33dc0685965ce534";
let lambdaUrl;

const infraEnv = getInfraDev();

// OD / ephemeral: always prefer the env-specific backend when INFRA_ENV is set
if (infraEnv) {
  lambdaUrl = `https://checkout-standalone-backend-${infraEnv}.dev.appcharge-int.com`;
} else {
  switch (env) {
    case "staging":
      lambdaUrl =
        "https://n8iiv57d99.execute-api.us-east-1.amazonaws.com/ac-stg";
      break;
    case "sandbox":
      lambdaUrl =
        "https://a3no38e5c1.execute-api.us-east-1.amazonaws.com/sandbox";
      break;
    case "production":
      lambdaUrl = "https://myf6de8jsh.execute-api.us-east-1.amazonaws.com/prod";
      break;
    case "dev":
    default:
      lambdaUrl = "https://rryhv338ih.execute-api.us-east-1.amazonaws.com/dev";
      break;
  }
}

const MOCK_DATA = {
  customer: {
    id: "John Doe",
    email: "test@appcharge.com",
  },
  priceDetails: {
    price: 1550,
    currency: "usd",
  },
  offer: {
    name: "Crazy Pack",
    sku: "CrazyPack",
    assetUrl:
      "https://media-dev.appcharge.com/media/64ad4f25cc1a482bac467ae5/fire-fire-logo-fire-fire-icon-fire-sign-fire-symbol-transparent-background-ai-generative-free-png.png",
    description: "Crazy Pack Bundle",
  },
  items: [
    {
      name: "Coins",
      assetUrl: "https://media-dev.appcharge.com/media/product-3.png",
      sku: "coins_xoxoxo",
      quantity: 6580,
    },
    {
      name: "Tokens",
      assetUrl: "https://media-dev.appcharge.com/media/bills.png",
      sku: "coins_xoxoxo",
      quantity: 150,
    },
    {
      name: "Boosters",
      assetUrl: "https://media-dev.appcharge.com/media/booster.png",
      sku: "coins_xoxoxo",
      quantity: 3,
    },
  ],
  sessionMetadata: {},
};

const searchParams = new URLSearchParams(window.location.search);
const isSupercellTemplate = searchParams.get("template") === "supercell";

const Home = () => {
  const navigate = useNavigate();
  const [isIframeOpen, setIsIframeOpen] = useState(false);
  const [session, setSession] = useState();
  const [isIframeLoading, setIsIframeLoading] = useState(false);
  const [formattedPricePoint, setFormattedPricePoint] = useState(null);
  const [isCheckoutV3On, setIsCheckoutV3On] = useState(true);
  const [price, setPrice] = useState(
    `$ ${(MOCK_DATA.priceDetails.price / 100).toFixed(2)}`,
  );
  const [customJSON, setCustomJSON] = useState(MOCK_DATA);
  const [isJSONdrawerOpen, setIsJSONdrawerOpen] = useState(false);
  const [isAdvancedOptionsOpen, setIsAdvancedOptionsOpen] = useState(false);
  const [locale, setLocale] = useState("");
  const [customCheckoutUrl, setCustomCheckoutUrl] = useState(
    `https://checkout-v2-${env}.appcharge.com`,
  );
  const [customSessionToken, setCustomSessionToken] = useState("");
  const [customCheckoutToken, setCustomCheckoutToken] = useState(checkoutToken);
  const [checkoutBackground, setCheckoutBackground] = useState("");
  const [isMobileSDKMode, setIsMobileSDKMode] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(870);
  const handleLogout = () => {
    localStorage.removeItem("__ac_user");
    navigate("/login");
  };

  const clickHandler = useCallback(() => {
    setIsIframeLoading(true);
    setCustomCheckoutToken("");
    setCustomSessionToken("");
    createCheckoutSession();
  }, [customJSON, isCheckoutV3On]);

  const createCheckoutSession = () => {
    axios
      .post(`${lambdaUrl}/createSession`, customJSON, {
        headers: {
          "x-publisher-token": publisherToken,
        },
      })
      .catch((err) => console.log(err))
      .then((res) => {
        setIsIframeOpen(true);
        let url = isCheckoutV3On
          ? generateCheckoutV3Url(res.data.parsedUrl)
          : res.data.parsedUrl;

        if (isSupercellTemplate) {
          url = addTemplateToUrl(url);
        }

        setSession({
          token: res.data.checkoutSessionToken,
          url: url,
        });
      });
  };

  const addTemplateToUrl = (url) => {
    const newUrl = new URL(url);
    newUrl.searchParams.set("template", "supercell");
    return newUrl.toString();
  };

  const generateCheckoutV3Url = (parsedUrl) => {
    const newOrigin = CHECKOUT_V3_URL_MAP[env];
    // OD/ephemeral envs have no static mapping; the backend already returns the
    // correct env-specific pay origin, so leave parsedUrl untouched.
    if (!newOrigin) return parsedUrl;
    return parsedUrl.replace(/^https?:\/\/[^/]+/, newOrigin);
  };

  const getPricePointsHandler = async () => {
    const res = await getPricePoints(env, checkoutToken);
    const relevantPrice = res.pricingPoints.find(
      (p) => Number(p.basePriceInUSD) === customJSON.priceDetails.price,
    );
    if (!relevantPrice) return;
    const newJSON = { ...customJSON };
    newJSON.priceDetails.price = relevantPrice.localizedPrice;
    newJSON.priceDetails.currency = res.pricingPointData.currencyCode;
    setCustomJSON(newJSON);
    setFormattedPricePoint(relevantPrice.formattedPrice);
  };

  const runCheckoutHandler = () => {
    setIsIframeOpen(true);
    setIsJSONdrawerOpen(false);
    setIsAdvancedOptionsOpen(false);
    setSession({
      token: customSessionToken,
      url: customCheckoutUrl,
    });
  };

  useEffect(() => {
    window.addEventListener("message", receiveMessage, false);

    function receiveMessage(event) {
      const dataFromIframe = event.data;
      if (dataFromIframe.event === "appcharge_checkout_dimensions_changed") {
        setIframeHeight(dataFromIframe.params.height);
      }
    }
  }, []);

  useEffect(() => {
    setPrice(`$ ${(customJSON.priceDetails.price / 100).toFixed(2)}`);
  }, [customJSON]);

  useEffect(() => {
    if (isSupercellTemplate) {
      clickHandler();
    }
  }, [clickHandler, isSupercellTemplate]);

  return (
    <>
      <Drawer
        anchor={"right"}
        open={isAdvancedOptionsOpen}
        onClose={() => setIsAdvancedOptionsOpen(false)}
      >
        <Stack
          width={"400px"}
          height={"100%"}
          className="json-drawer"
          style={{
            backgroundColor: "#1e1e1e",
          }}
          p={2}
          gap={2}
        >
          <Box>
            <h2 style={{ color: "white" }}>Advanced Options</h2>
          </Box>
          <Typography color="white">Session Token</Typography>
          <Input
            type="text"
            placeholder="Session Token"
            value={customSessionToken}
            onChange={(e) => setCustomSessionToken(e.target.value)}
            sx={{
              background: "white",
              padding: "0.5em",
              borderRadius: "0.5em",
            }}
          />
          <Typography color="white">Checkout URL</Typography>
          <Input
            type="text"
            placeholder="Checkout URL"
            value={customCheckoutUrl}
            onChange={(e) => setCustomCheckoutUrl(e.target.value)}
            sx={{
              background: "white",
              padding: "0.5em",
              borderRadius: "0.5em",
            }}
          />
          <Typography color="white">Checkout Token</Typography>
          <Input
            type="text"
            placeholder="Checkout Token"
            value={customCheckoutToken}
            onChange={(e) => setCustomCheckoutToken(e.target.value)}
            sx={{
              background: "white",
              padding: "0.5em",
              borderRadius: "0.5em",
            }}
          />
          <Typography color="white">Checkout Background Color</Typography>
          <Input
            type="text"
            placeholder="Checkout Background Color"
            value={checkoutBackground}
            onChange={(e) => setCheckoutBackground(e.target.value)}
            sx={{
              background: "white",
              padding: "0.5em",
              borderRadius: "0.5em",
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={isMobileSDKMode}
                onChange={(e) => setIsMobileSDKMode(e.target.checked)}
              />
            }
            label={<Typography color="white">mobile SDK mode</Typography>}
          />
          <Button variant="contained" onClick={runCheckoutHandler}>
            Run Checkout
          </Button>
        </Stack>
      </Drawer>
      <Drawer
        anchor={"right"}
        open={isJSONdrawerOpen && !isAdvancedOptionsOpen}
        onClose={() => setIsJSONdrawerOpen(false)}
      >
        <Stack
          width={"600px"}
          className="json-drawer"
          style={{
            backgroundColor: "#1e1e1e",
          }}
          py={2}
        >
          <Stack direction="row" justifyContent="flex-end" pr={2}>
            <Button
              onClick={() => setIsAdvancedOptionsOpen(true)}
              variant="outlined"
              sx={{
                maxWidth: 200,
              }}
            >
              Advanced Options
            </Button>
          </Stack>
          <Stack
            p={2}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            style={{
              backgroundColor: "#1e1e1e",
            }}
          >
            <h2 style={{ color: "white" }}>Edit Offer JSON</h2>
            <Button
              onClick={() => setIsJSONdrawerOpen(false)}
              sx={{
                color: "white",
                fontSize: "1rem",
              }}
              variant="contained"
            >
              SAVE
            </Button>
          </Stack>
          <Stack px={2} pb={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={isCheckoutV3On}
                  onChange={(e) => setIsCheckoutV3On(e.target.checked)}
                />
              }
              label={<Typography color="white">Use Checkout V3</Typography>}
            />
          </Stack>
          <JSONInput
            onBlur={(e) => setCustomJSON(e.jsObject)}
            id="a_unique_id"
            placeholder={customJSON}
            theme="dark"
            locale={locale}
            width="100%"
            height="100%"
            waitAfterKeyPress={300000}
            style={{
              body: {
                fontSize: "16px",
              },
            }}
          />
        </Stack>
      </Drawer>
      <div className="wrapper">
        <div
          className="loading-iframe"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.75)",
            zIndex: 999999999,
            display: isIframeLoading && !isSupercellTemplate ? "flex" : "none",
            justifyContent: "center",
            alignItems: "center",
            color: "white",
            fontSize: "2rem",
          }}
        >
          <span className="loader"></span>
        </div>
        {isIframeOpen && !isSupercellTemplate && (
          <motion.div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100vh",
            }}
            initial={{ zIndex: 9999999 }}
          >
            <AppchargeCheckout
              mode={isMobileSDKMode ? "mobile-sdk" : ""}
              onInitialLoad={() => setIsIframeLoading(false)}
              onOrderCreated={(params) => {
                console.log("onOrderCreated", { params });
                setIsIframeLoading(false);
              }}
              onClose={(params) => {
                console.log("onClose", { params });
                setIsIframeOpen(false);
              }}
              onPaymentButtonClicked={(params) => {
                console.log("onPaymentButtonClicked", { params });
              }}
              onOrderCompletedFailed={(params) =>
                console.log("onOrderCompletedFailed", { params })
              }
              onOrderCompletedSuccessfully={(params) =>
                console.log("onOrderCompletedSuccessfully", { params })
              }
              onPaymentIntentFailed={(params) =>
                console.log("onPaymentIntentFailed", { params })
              }
              onPaymentIntentSuccess={(params) =>
                console.log("onPaymentIntentSuccess", { params })
              }
              onPaymentIntent={(params) =>
                console.log("onPaymentIntent", { params })
              }
              checkoutStyle={{
                overlayBackgroundColor: checkoutBackground,
              }}
              checkoutUrl={session?.url}
              locale={locale}
            />
          </motion.div>
        )}
        <header>
          <div>
            <img src={profile} alt="profile" />
          </div>
          <div>
            <img
              onClick={() => setIsJSONdrawerOpen(true)}
              src={logo}
              className="logo"
              alt="logo"
            />
          </div>
          <div>
            <Stack width={"150px"} gap={2}>
              <button onClick={getPricePointsHandler}>Get price points</button>
              <select
                value={locale}
                onChange={(e) => {
                  setLocale(e.target.value);
                }}
              >
                <option value="">Language</option>
                <option value="en">English</option>
                <option value="ja">Japanese</option>
                <option value="ko">Korean</option>
                <option value="de">German</option>
              </select>

              <div>
                <Stack width={"150px"} gap={2} direction="column">
                  <input
                    type="text"
                    placeholder="Redirect URL"
                    onChange={(e) => {
                      if (e.target.value) {
                        setCustomJSON({
                          ...customJSON,
                          redirectUrl: e.target.value,
                        });
                      } else {
                        setCustomJSON({
                          ...customJSON,
                          redirectUrl: undefined,
                        });
                      }
                    }}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      fontSize: "14px",
                      width: "100%",
                      transition: "border-color 0.2s ease",
                      boxSizing: "border-box",
                      outline: "none",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#666")}
                    onBlur={(e) => (e.target.style.borderColor = "#ccc")}
                  />
                  <span style={{ fontSize: "12px", color: "white" }}>
                    Note: Redirect URL feature requires feature flag to be
                    enabled
                  </span>
                </Stack>
              </div>
            </Stack>
          </div>
          <div>
            <Button
              variant="outlined"
              onClick={handleLogout}
              sx={{
                color: "white",
                borderColor: "white",
                "&:hover": {
                  borderColor: "#ccc",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              Logout
            </Button>
          </div>
        </header>
        <section className="offers">
          <img src={offer1} alt="offer1" />
          <img src={offer2} alt="offer2" />
          <img src={offer3} alt="offer3" />
        </section>
        <h2 style={{ color: "white" }}>Offers</h2>
        <section className="bundles">
          <div>
            <img
              onClick={() => clickHandler()}
              src={bundle}
              alt="bundle"
              data-testid="offer-1"
            />
            <div className="price">{formattedPricePoint || price}</div>
          </div>
          <div>
            <img
              onClick={() => clickHandler()}
              src={bundle}
              alt="bundle"
              data-testid="offer-2"
            />
            <div className="price">{formattedPricePoint || price}</div>
          </div>
          <div>
            <img
              onClick={() => clickHandler()}
              src={bundle}
              alt="bundle"
              data-testid="offer-3"
            />
            <div className="price">{formattedPricePoint || price}</div>
          </div>
        </section>
        <h2 style={{ color: "white", marginBottom: "1em" }}>Subscriptions</h2>
        <Subscriptions />
        {isSupercellTemplate && (
          <section className="iframe-section">
            {session?.url && (
              <iframe
                id="supercell-iframe"
                // width="100vw"
                // height="100%"
                src={session?.url}
                title="Embedded Content"
                style={{
                  width: "100vw",
                  border: "none",
                  margin: "20px 0",
                  height: "100vh",
                  // minHeight: iframeHeight,
                  maxHeight: iframeHeight,
                }}
              />
            )}
          </section>
        )}
      </div>
    </>
  );
};

export default Home;
